import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { shallowEqual, useDispatch } from 'react-redux';

import { cloneDeep } from 'es-toolkit';
import { logError } from '@zg-rentals/log-error';

import { RMWContext } from '../../providers/RMWContext';

import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import propertyUtils from '../../utils/propertyUtils';

import WebSocketMessageNameConstants from '../../constants/WebSocketMessageNameConstants';

import AddPropertyModal from '../Modal/AddPropertyModal';
import AddPropertyConstants from '../../constants/AddPropertyConstants';

type Props = {
  isInvokedFromSF?: boolean;
};

const PropertyDetailsHOC = ({ isInvokedFromSF = false }: Props) => {
  const dispatch = useDispatch();

  const location = useLocation();

  const { paths } = useContext(RMWContext);

  const {
    currentProperty,
    currentProperty: { active, listingAlias, propertyTypeCode, isMultifamily },
  } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
    }),
    shallowEqual,
  );

  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);

  useEffect(() => {
    if (listingAlias && propertyTypeCode !== -1) {
      if (!active) {
        dispatch(PropertiesActions.getCurrentPropertyAddressRestrictions(currentProperty));
      }

      window.rentalManagerApiWebSocket.addEventListener('message', handleRentalManagerApiWebSocket);

      if (currentProperty?.isAddressValid === false) {
        setDisplayAddPropertyModal(true);
      }

      return () => {
        window.rentalManagerApiWebSocket.removeEventListener('message', handleRentalManagerApiWebSocket);
      };
    }
  }, [listingAlias, propertyTypeCode]);

  useEffect(() => {
    if (!isInvokedFromSF && listingAlias && !isMultifamily) {
      const isInReviews = Boolean(location?.pathname?.includes('/reviews'));

      logError({
        error: JSON.stringify({
          listingAlias,
        }),
        errorType: isInReviews ? 'SFListingLoadingReviews' : 'IncorrectUILoadingError',
        context: isInReviews ? 'PropertyDetailsHOC.SFListingReviews' : 'PropertyDetailsHOC.SFListingInMF',
      });
      setTimeout(() => {
        if (isInReviews) {
          window.router.redirectTo({
            pathname: `${paths.propertiesPage}/${listingAlias}/dashboard/overview`,
          });
        } else {
          window.location.reload();
        }
      }, 200);
    }
  }, [isInvokedFromSF, isMultifamily, listingAlias, location, paths]);

  const handleRentalManagerApiWebSocket = (message: { data: string }) => {
    // update listing status section with syndication status updates.
    let data;
    try {
      data = JSON.parse(message.data);
    } catch (error) {
      return;
    }
    if (data.name !== WebSocketMessageNameConstants.LISTING_SYNDICATION_STATUS) {
      return;
    }
    const { payload } = data;
    const { feedId, feedListingId } = payload;

    // Don't do anything if the message is for a different listing
    if (listingAlias !== payload.listingAlias && listingAlias !== propertyUtils.getListingId(feedId, feedListingId)) {
      return;
    }

    const syndicationPayload = payload.userPropertyDetailsSyndicationDto;
    const syndicationCopy = cloneDeep(currentProperty.syndication);

    syndicationCopy.hotpads = syndicationPayload.hotpads;
    syndicationCopy.trulia = syndicationPayload.trulia;
    syndicationCopy.zillow = syndicationPayload.zillow;

    // external
    syndicationCopy.realtor = syndicationPayload.realtor;
    syndicationCopy.rent = syndicationPayload.rent;
    syndicationCopy.redfin = syndicationPayload.redfin;
    syndicationCopy.apartmentGuide = syndicationPayload.apartmentGuide;

    syndicationCopy.universal = syndicationPayload.universal;
    syndicationCopy.overview = syndicationPayload.overview;
    syndicationCopy.customerProblems = syndicationPayload.customerProblems;

    dispatch(PropertiesActions.updateCurrentProperty({ syndication: syndicationCopy }));
  };

  return (
    <AddPropertyModal
      isOpen={displayAddPropertyModal}
      onClose={() => setDisplayAddPropertyModal(false)}
      behavior={AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT}
    />
  );
};

export default PropertyDetailsHOC;
